import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, MapPin, Plus, Pencil, Trash2, Lock, ChefHat, StickyNote, Users, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const DAYS = { fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"], nl: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"], en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] };
const MONTHS = { fr: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"], nl: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"], en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] };
const MOMENTS = { fr: ["Matin", "Après-midi"], nl: ["Ochtend", "Namiddag"], en: ["Morning", "Afternoon"] };
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const dow = x.getDay(); x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow)); x.setHours(0, 0, 0, 0); return x; };
const emptyForm = { id: null, name: "", start: "", end: "", place: "", responsables: "", day: 0, moment: "morning" };

function AnalogClock({ now }) {
  const s = now.getSeconds(), m = now.getMinutes(), h = now.getHours() % 12;
  const sa = s * 6, ma = m * 6 + s * 0.1, ha = h * 30 + m * 0.5;
  const hand = (ang, len, w, col) => { const r = (ang - 90) * Math.PI / 180; return <line x1="50" y1="50" x2={50 + len * Math.cos(r)} y2={50 + len * Math.sin(r)} stroke={col} strokeWidth={w} strokeLinecap="round" />; };
  return (
    <svg viewBox="0 0 100 100" className="h-20 w-20 drop-shadow">
      <circle cx="50" cy="50" r="48" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
      {Array.from({ length: 12 }).map((_, i) => { const a = (i * 30 - 90) * Math.PI / 180; return <circle key={i} cx={50 + 40 * Math.cos(a)} cy={50 + 40 * Math.sin(a)} r={i % 3 === 0 ? 2.2 : 1.2} fill="rgba(255,255,255,0.85)" />; })}
      {hand(ha, 24, 3.4, "#ffffff")}{hand(ma, 33, 2.4, "#ffffff")}{hand(sa, 37, 1, "#fdba74")}
      <circle cx="50" cy="50" r="2.6" fill="#fdba74" />
    </svg>
  );
}

function CuisinePostits({ dates, dayLabels }) {
  const { t } = useTranslation();
  const [postits, setPostits] = useState([]);
  const [addFor, setAddFor] = useState(null);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/cuisine/postits", { params: { frm: dates[0], to: dates[6] } }); setPostits(data.postits || []); }
    catch { setPostits([]); }
  }, [dates]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/cuisine/postits", { date: addFor, text: text.trim() }); setText(""); setAddFor(null); load(); toast.success(t("cuisine.postitAdded")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };
  const remove = async (p) => {
    try { await api.delete(`/cuisine/postits/${p.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };
  return (
    <div data-testid="cuisine-postits">
      <div className="flex items-center gap-2 mb-2 text-muted-foreground">
        <StickyNote className="h-4 w-4 text-amber-400" />
        <span className="font-semibold text-sm">{t("cuisine.postits")}</span>
        <span className="text-xs">— {t("cuisine.postitsHint")}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
        {dates.map((d, i) => (
          <div key={d} className="rounded-xl bg-white text-slate-800 p-2 min-h-[86px] shadow" data-testid={`cuisine-postit-day-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase text-slate-400">{dayLabels[i].slice(0, 3)} {d.slice(8, 10)}</span>
              <button data-testid={`cuisine-postit-add-${i}`} onClick={() => { setAddFor(d); setText(""); }}
                className="h-6 w-6 rounded-full bg-amber-400 text-white flex items-center justify-center hover:bg-amber-500 transition"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            {postits.filter((p) => p.date === d).map((p) => (
              <div key={p.id} className="mt-1.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 p-1.5 group relative" data-testid={`cuisine-postit-${p.id}`}>
                <p className="text-[11px] leading-snug whitespace-pre-wrap break-words">{p.text}</p>
                <p className="text-[9px] text-slate-400 mt-0.5">— {p.author_name}</p>
                {p.can_delete && (
                  <button onClick={() => remove(p)} data-testid={`cuisine-postit-del-${p.id}`}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition"><Trash2 className="h-3 w-3" /></button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      <Dialog open={!!addFor} onOpenChange={(o) => !o && setAddFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("cuisine.postitNew")}</DialogTitle></DialogHeader>
          <textarea data-testid="cuisine-postit-text" className="w-full min-h-[110px] rounded-md border px-3 py-2 bg-background text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("cuisine.postitPlaceholder")} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddFor(null)}>{t("planning.cancel")}</Button>
            <Button data-testid="cuisine-postit-save" onClick={add}>{t("planning.add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CuisineAgenda() {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const lng = (i18n.language || "fr").slice(0, 2);
  const L = DAYS[lng] || DAYS.fr, ML = MONTHS[lng] || MONTHS.fr, MO = MOMENTS[lng] || MOMENTS.fr;
  const [now, setNow] = useState(new Date());
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [entries, setEntries] = useState([]);
  const [maison, setMaison] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const load = useCallback(async (mon) => {
    try { const { data } = await api.get("/cuisine/entries", { params: { week_start: iso(mon) } }); setEntries(data.entries || []); if (data.maison) setMaison(data.maison); }
    catch { setEntries([]); }
  }, []);
  useEffect(() => { load(monday); }, [monday, load]);

  const shift = (days) => { const d = new Date(monday); d.setDate(d.getDate() + days); setMonday(mondayOf(d)); };
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const period = monday.getMonth() === sunday.getMonth()
    ? `${pad(monday.getDate())}–${pad(sunday.getDate())} ${ML[sunday.getMonth()]} ${sunday.getFullYear()}`
    : `${pad(monday.getDate())} ${ML[monday.getMonth()]} – ${pad(sunday.getDate())} ${ML[sunday.getMonth()]} ${sunday.getFullYear()}`;
  const digital = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  const dateLbl = `${L[(now.getDay() + 6) % 7]} ${now.getDate()} ${ML[now.getMonth()]} ${now.getFullYear()}`;
  const cell = (di, mo) => entries.filter((e) => e.day === di && e.moment === mo);

  const openAdd = (di, mo) => { setForm({ ...emptyForm, day: di, moment: mo, responsables: user?.name || "" }); setOpen(true); };
  const openEdit = (e) => { if (!e.can_edit) return; setForm({ id: e.id, name: e.name || "", start: e.start || "", end: e.end || "", place: e.place || "", responsables: e.responsables || "", day: e.day, moment: e.moment }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error(t("cuisine.nameRequired"));
    const payload = { week_start: iso(monday), day: form.day, moment: form.moment, name: form.name.trim(), start: form.start, end: form.end, place: form.place, responsables: form.responsables };
    try {
      if (form.id) { await api.put(`/cuisine/entries/${form.id}`, payload); toast.success(t("cuisine.taskUpdated")); }
      else { await api.post("/cuisine/entries", payload); toast.success(t("cuisine.taskSaved")); }
      setOpen(false); await load(monday);
    } catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };
  const remove = async (e) => {
    if (!e.can_edit) return;
    if (!window.confirm(t("cuisine.confirmDelete", { name: e.name }))) return;
    try { await api.delete(`/cuisine/entries/${e.id}`); toast.success(t("cuisine.taskDeleted")); await load(monday); }
    catch (err) { toast.error(err?.response?.data?.detail || t("cuisine.saveFailed")); }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="cuisine-agenda">
      <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #431407 0%, #9a3412 55%, #ea580c 100%)" }}>
        <div className="absolute -right-10 -top-10 h-52 w-52 rounded-full" style={{ background: "radial-gradient(circle, rgba(253,186,116,0.28), transparent 70%)" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 flex items-center gap-1.5"><ChefHat className="h-3.5 w-3.5" /> {maison || t("planning.residence")}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">{t("nav.cuisineAgenda")}</h1>
            <p className="text-white/70 text-sm mt-1 capitalize">{dateLbl}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-mono text-4xl sm:text-5xl font-bold tabular-nums tracking-tight" data-testid="cuisine-agenda-clock">{digital}</div>
              <div className="text-white/60 text-xs mt-1">{period}</div>
            </div>
            <AnalogClock now={now} />
          </div>
        </div>
        <div className="relative flex items-center gap-2 mt-5 flex-wrap">
          <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="cuisine-agenda-prev" onClick={() => shift(-7)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="cuisine-agenda-today" onClick={() => setMonday(mondayOf(new Date()))}>{t("planning.today")}</Button>
          <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="cuisine-agenda-next" onClick={() => shift(7)}><ChevronRight className="h-4 w-4" /></Button>
          <span className="ml-2 text-white/80 text-sm flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {period} · {entries.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {L.map((d, di) => {
          const dt = new Date(monday); dt.setDate(monday.getDate() + di);
          const isToday = iso(dt) === iso(now);
          return (
            <Card key={di} className={`overflow-hidden ${isToday ? "ring-2 ring-orange-500" : ""}`}>
              <div className="text-center py-2 text-white" style={{ background: isToday ? "#ea580c" : "#c2410c" }}>
                <div className="font-semibold text-sm">{d}</div>
                <div className="text-[11px] text-white/80">{pad(dt.getDate())} {ML[dt.getMonth()]}</div>
              </div>
              {["morning", "afternoon"].map((mo, mi) => (
                <div key={mo} data-testid={`cuisine-cell-${di}-${mo}`} onClick={() => openAdd(di, mo)} title={t("planning.add")} className="p-2 border-t min-h-[96px] bg-orange-50/40 group/cell cursor-pointer transition-colors hover:bg-orange-100/70">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase font-bold text-orange-700/70">{MO[mi]}</div>
                    <button data-testid={`cuisine-add-${di}-${mo}`} onClick={(ev) => { ev.stopPropagation(); openAdd(di, mo); }} className="opacity-0 group-hover/cell:opacity-100 transition text-orange-600 hover:text-orange-800" title={t("planning.add")}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  {cell(di, mo).map((e) => (
                    <div key={e.id} onClick={(ev) => ev.stopPropagation()} className="mt-1.5 rounded-lg bg-white shadow-sm ring-1 ring-slate-100 p-2 group/e cursor-default" data-testid={`cuisine-entry-${e.id}`}>
                      <div className="flex items-start justify-between gap-1">
                        <div className="font-semibold text-xs text-slate-800 leading-tight flex items-center gap-1">{!e.can_edit && <Lock className="h-2.5 w-2.5 opacity-50 shrink-0" />}{e.name}</div>
                        {e.can_edit && (
                          <div className="flex gap-1 opacity-0 group-hover/e:opacity-100 shrink-0">
                            <button data-testid={`cuisine-edit-${e.id}`} onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} className="text-blue-600"><Pencil className="h-3 w-3" /></button>
                            <button data-testid={`cuisine-del-${e.id}`} onClick={(ev) => { ev.stopPropagation(); remove(e); }} className="text-red-500"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        )}
                      </div>
                      {(e.start || e.place) && <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">{e.start && <><Clock className="h-2.5 w-2.5" />{e.start}{e.end ? `-${e.end}` : ""}</>}{e.place && <><MapPin className="h-2.5 w-2.5 ml-1" />{e.place}</>}</div>}
                      {(e.responsables || e.created_by_name) && <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-medium bg-orange-100 text-orange-800"><Users className="h-2.5 w-2.5" />{e.responsables || e.created_by_name}</div>}
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          );
        })}
      </div>

      <CuisinePostits
        dates={Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return iso(d); })}
        dayLabels={L}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? t("cuisine.editTask") : t("cuisine.addTask")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("cuisine.taskName")}</Label>
              <Input data-testid="cuisine-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("cuisine.taskPlaceholder")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("planning.start")}</Label><Input data-testid="cuisine-form-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} placeholder="10h30" /></div>
              <div><Label>{t("planning.end")}</Label><Input value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} placeholder="11h30" /></div>
            </div>
            <div><Label>{t("planning.place")}</Label><Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder={t("cuisine.placePlaceholder")} /></div>
            <div><Label>{t("cuisine.responsables")}</Label><Input data-testid="cuisine-form-responsables" value={form.responsables} onChange={(e) => setForm({ ...form, responsables: e.target.value })} placeholder={user?.name || ""} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("planning.day")}</Label>
                <select className="w-full h-10 rounded-md border px-3 bg-background" value={form.day} onChange={(e) => setForm({ ...form, day: Number(e.target.value) })}>{L.map((d, i) => <option key={i} value={i}>{d}</option>)}</select>
              </div>
              <div><Label>{t("planning.moment")}</Label>
                <select className="w-full h-10 rounded-md border px-3 bg-background" value={form.moment} onChange={(e) => setForm({ ...form, moment: e.target.value })}><option value="morning">{MO[0]}</option><option value="afternoon">{MO[1]}</option></select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("planning.cancel")}</Button>
            <Button data-testid="cuisine-form-save" onClick={save}>{form.id ? t("planning.editSave") : t("planning.add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
